import { useGenerateHPTARXlsx, useRFS } from "@/api/rfs";
import { FieldFile } from "@/components/form/field-file";
import { FormContainer } from "@/components/form/form-container";
import {
  DefaultButton,
  DownloadButton,
  SubmitButton,
} from "@/components/ui/button";
import { VerticalGaps } from "@/components/ui/container";
import { DataInfo } from "@/components/ui/data-info";
import { MISSING_ID_IN_ROUTE, ROLE, STATUS } from "@/utils/constant";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { swalErrorDefault } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";
import {
  HPTARData,
  HPTARInvalidData,
  useGenerateHPTARInvalidXlsx,
  useHPTARGeneralMessage,
  useInfiniteHPTARInvalids,
  useInfiniteHPTARs,
  useInfiniteKmzInvalids,
} from "@/api/hptar";
import { Badge } from "@/components/ui/badge";
import { useCurrentUser } from "../auth/useCurrentUser";
import { renderTableHeader, renderTableRow } from "./renderTable";
import { FormItem, FormLabel } from "@/components/form/form";
import { cn } from "@/utils/helper";
import { Checkbox } from "@/components/ui/checkbox";
import useCreateHPTAR from "./hooks/useCreateHPTAR";
import { RequiredAsterisk } from "@/components/form/required-asterisk";
import CableDistributionField from "./CableDistributionField";
import { useHistoryUpdateHomepass } from "@/api/history-update-homepass";
import { formatDateTime } from "@/utils/helper/formatDate";
import useDownloadABDKmz from "./hooks/useDownloadABDKmz";
import { isHPTARValidated } from "../rfs/document/utils/validation";
import HPTARKMZInvalidsTable from "./HPTARKMZInvalidsTable";

const hptarFormSchema = z
  .object({
    fileList: z.instanceof(FileList).optional(),
    hptar: z
      .object({
        isHptarNoUpdated: z.boolean(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const isHptarNoUpdated = data.hptar?.isHptarNoUpdated;
    const fileList = data.fileList;

    if (!isHptarNoUpdated && (!fileList || fileList.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either upload a file or confirm that there are no updates.",
        path: ["fileList"],
      });
    }
  });

export type HPTARFormSchema = z.infer<typeof hptarFormSchema>;

export function HPTARCreateForm() {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }
  const { roles } = useCurrentUser();
  const [hptarInvalidsRef, setHptarInvalidsRef] = useState<HPTARInvalidData[]>(
    [],
  );
  const [hptarsRef, setHptarsRef] = useState<HPTARData[]>([]);

  const { data: rfs } = useRFS({ id });
  const {
    data: hptars,
    hasNextPage: hasNextPageHPTARs,
    fetchNextPage: fetchNextHPTARs,
  } = useInfiniteHPTARs({ rfsId: id });
  const { data: historyUpdateHomepass } = useHistoryUpdateHomepass({
    rfsId: id,
  });

  const { data: generalMessages } = useHPTARGeneralMessage({
    rfsId: id,
  });
  const {
    data: hptarInvalids,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteHPTARInvalids({ rfsId: id });
  const { data: kmzInvalids } = useInfiniteKmzInvalids({ rfsId: id });

  const { mutate: generateHPTAR, isPending: isDownloading } =
    useGenerateHPTARXlsx();
  const { mutate: downloadHPTARInvalid, isPending: isDownloadingInvalid } =
    useGenerateHPTARInvalidXlsx();

  useEffect(() => {
    if (hptars?.pages) {
      const allContent = hptars?.pages.flatMap((page) => page.content);
      setHptarsRef(allContent);
    }
    if (hptarInvalids?.pages) {
      const allContent = hptarInvalids.pages.flatMap((page) => page.content);
      setHptarInvalidsRef(allContent);
    }
  }, [hptarInvalids, kmzInvalids, hptars]);

  useEffect(() => {
    if (!hasNextPageHPTARs) return;

    fetchNextHPTARs();
  }, [hasNextPageHPTARs, fetchNextHPTARs]);

  const rfsStatus = rfs?.statusRfsSubmission;
  const isAllowed = hasCommonElement(roles, [
    ROLE.ADMINISTRATOR,
    ROLE.REVISE_DOCUMENT_OFFICER,
    ROLE.VENDOR,
    ROLE.VALIDATOR_DOCUMENT_OFFICER,
  ]);
  const isValidated = isHPTARValidated({
    hptars: hptarsRef,
    hptarInvalids: hptarInvalidsRef,
    hptarGeneralMessages: generalMessages,
    kmzInvalids: kmzInvalids?.pages?.[0]?.content,
  });

  const isReadonly =
    !isAllowed ||
    rfsStatus === STATUS.RFS.REVISION_BY_LN ||
    rfsStatus === STATUS.RFS.GENERATING_HPDB;

  const hptar = hptarsRef?.[0];
  const lastUpdated = hptar?.updatedDate;
  const historyUpdate = historyUpdateHomepass?.[0];
  const filePathExisting = historyUpdate?.fileHptar ?? undefined;
  const isNotCreatedHPTAR = historyUpdate?.status !== "Create RFS / Created";

  const canDownloadExistingHptar = Boolean(
    filePathExisting && isNotCreatedHPTAR,
  );

  const defaultFileName =
    canDownloadExistingHptar && filePathExisting
      ? filePathExisting.split("/").pop() ?? "file.kmz"
      : undefined;

  const form = useForm<HPTARFormSchema>({
    resolver: zodResolver(hptarFormSchema),
  });

  const isHptarNoUpdated = form.watch("hptar.isHptarNoUpdated");
  const fileList = form.watch("fileList");

  const logicDisableSubmitButton = !isHptarNoUpdated && !fileList?.length;
  const disableFileUpload = isHptarNoUpdated;

  function handleDownload() {
    if (!id) {
      throw new Error(MISSING_ID_IN_ROUTE);
    }
    generateHPTAR(
      { id, name: `HPTAR-${rfs?.clusterId}.xlsx` },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  }

  function handleDownloadHPTARInvalid() {
    if (!id) {
      throw new Error(MISSING_ID_IN_ROUTE);
    }

    downloadHPTARInvalid({ id, name: `error-HPTAR-${rfs?.clusterId}.xlsx` });
  }

  const { handleSubmit } = useCreateHPTAR({ id });
  const {
    handleDownload: handleDownloadABDKmz,
    isPending: isDownloadingABDKmz,
  } = useDownloadABDKmz({ id });

  return (
    <>
      <VerticalGaps>
        <div className="flex">
          <div className="flex flex-1 flex-col">
            <div className="grid grid-cols-4 gap-y-8">
              <DataInfo label="Cluster ID" value={rfs?.clusterId} />
              <DataInfo label="Total HP RFS" value={rfs?.totalHpRfs} />
              <DataInfo
                label="Total HP HOME"
                value={rfs?.totalResidenceHomepass}
              />
              <DataInfo
                label="Total HP HOME-BIZ"
                value={rfs?.totalBizzHomepass}
              />
              <DataInfo label="Project Name" value={rfs?.projectName} />
              <DataInfo
                label="Total HP APD"
                value={rfs?.trackerdataApd?.hpDesign}
              />
              <DataInfo
                label="Total HP ADDITIONAL"
                value={rfs?.totalHomepassAdditional}
              />
              <DataInfo
                label="Total HP INVALID"
                value={rfs?.totalHomepassInvalid}
              />
            </div>
          </div>
          <div className="flex w-1/3 flex-none flex-col items-end gap-y-8">
            <div className="flex items-center gap-x-1">
              <DownloadButton onClick={handleDownload} loading={isDownloading}>
                Download HPTAR
              </DownloadButton>
              <DownloadButton
                onClick={handleDownloadABDKmz}
                loading={isDownloadingABDKmz}
              >
                Download ABD.kmz
              </DownloadButton>
            </div>

            <DataInfo label="Status">
              <Badge variant={isValidated ? "success" : "destructive"}>
                {isValidated ? "Validated" : "Unvalidated"}
              </Badge>
            </DataInfo>
          </div>
        </div>
      </VerticalGaps>
      <FormContainer form={form} onSubmit={handleSubmit}>
        <FormItem
          className={cn("flex flex-row items-start space-x-3 space-y-0")}
        >
          <Checkbox
            checked={isHptarNoUpdated}
            onCheckedChange={(checked) =>
              form.setValue("hptar.isHptarNoUpdated", Boolean(checked))
            }
            id={"hptar-is-updated"}
            disabled={isReadonly}
          />

          <FormLabel className="font-normal" htmlFor={"hptar-is-updated"}>
            I have downloaded and reviewed the HPTAR results from ABD.kmz.
            <br /> By checking this, I agree that the HPTAR data is valid and
            there are no updates.
          </FormLabel>
        </FormItem>

        <FieldFile
          label="Upload HPTAR (.xlsx)"
          name="fileList"
          accept=".xlsx"
          defaultName={defaultFileName}
          canDownloadExistingFile={canDownloadExistingHptar}
          pathExistingFile={filePathExisting}
          title={getTooltipTitle(isAllowed, rfsStatus)}
          isDisabled={isReadonly || disableFileUpload}
        />

        <div className="text-sm">
          Last Update: <b>{lastUpdated ? formatDateTime(lastUpdated) : "-"}</b>
        </div>

        <SubmitButton
          disabled={isReadonly || logicDisableSubmitButton}
          title={getTooltipTitle(isAllowed, rfsStatus)}
        />
      </FormContainer>

      {hptarInvalidsRef && hptarInvalidsRef?.length > 0 && (
        <>
          <span className="text-sm font-bold italic text-red-500">
            <RequiredAsterisk />
            please resubmit with the correct document!
          </span>

          <div className="flex items-center justify-between">
            <h5 className="mb-1 font-medium leading-none tracking-tight">
              HPTAR Validation Result
            </h5>

            <DownloadButton
              onClick={handleDownloadHPTARInvalid}
              loading={isDownloadingInvalid}
            >
              Download HPTAR Validation Result
            </DownloadButton>
          </div>

          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full table-auto">
              {renderTableHeader()}
              <tbody className="divide-y">
                {hptarInvalidsRef?.map(renderTableRow)}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-center">
            {hasNextPage && (
              <DefaultButton
                mood="axiata-blue-outline"
                className="mx-auto"
                loading={isFetchingNextPage}
                onClick={() => {
                  fetchNextPage();
                }}
              >
                {isFetchingNextPage ? "Loading..." : "Load More"}
              </DefaultButton>
            )}
          </div>
        </>
      )}

      {generalMessages && generalMessages?.length > 0 && (
        <div>
          <div className="mb-3 font-medium leading-none tracking-tight">
            General Message
          </div>
          <ol className="list-inside list-decimal">
            {generalMessages?.map((message) => (
              <li key={message.id}>{message.message}</li>
            ))}
          </ol>
        </div>
      )}

      <CableDistributionField isReadonly={isReadonly} />

      <HPTARKMZInvalidsTable />
    </>
  );
}

function getTooltipTitle(isAllowed: boolean, rfsStatus?: string) {
  if (rfsStatus === STATUS.RFS.REVISION_BY_LN) {
    return "In the review process";
  }
  if (!isAllowed) {
    return "You are not allowed to upload";
  }
}
